/**
 * Nav.tsx
 *
 * The bottom-nav-becomes-sidebar recipe (docs/DESIGN.md "Chrome relocates,
 * components don't fork"): the same five slots render in a row on phone/
 * tablet and in a column from `lg`. Icons, labels and handlers all arrive as
 * props — this package imports no icon library and no router.
 *
 * NavRoot is the `<nav>` landmark. NavItem is a destination tab. NavCentre is
 * the raised disc (student's Ask, parent's Scan) — kept a separate component
 * because it carries its own art slot and geometry, not a glyph + label like
 * every other tab. NavLogo is the `lg`/`xl` sidebar header (mark vs wordmark).
 */

import type { ReactNode } from 'react';

interface NavRootProps {
  label: string;
  children: ReactNode;
}

export function NavRoot({ label, children }: NavRootProps) {
  // relative z-10: the raised NavCentre disc (-mt-4) needs a stacking
  // context to paint above a tall scroll region's content — without it, on
  // phone/tablet the content painted OVER the disc. z-10 stays well under
  // every sheet/modal (SheetDialog/SideDrawer are z-60), so it never fights
  // those.
  return (
    <nav
      aria-label={label}
      className="relative z-10 flex items-stretch bg-surface lg:flex-col lg:h-full lg:py-4 lg:px-2 lg:gap-1 xl:px-3"
    >
      {children}
    </nav>
  );
}

interface NavItemProps {
  icon: ReactNode;
  activeIcon?: ReactNode;
  label: string;
  active: boolean;
  onSelect: () => void;
  /** Hide this tab below `lg` — a desktop-only sidebar entry. */
  hideBelowLg?: boolean;
}

export function NavItem({ icon, activeIcon, label, active, onSelect, hideBelowLg }: NavItemProps) {
  const glyph = active && activeIcon ? activeIcon : icon;
  return (
    <button
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      className={`flex-1 flex flex-col items-center gap-0.5 py-2 pb-3 rounded-md lg:flex-none lg:size-14 lg:justify-center lg:py-0 lg:pb-0 xl:size-auto xl:w-full xl:flex-row xl:justify-start xl:min-h-11 xl:px-3 xl:gap-3 ${
        active ? 'text-primary lg:bg-bg' : 'text-muted hover:bg-bg'
      } ${hideBelowLg ? 'hidden lg:flex' : ''}`}
    >
      {glyph}
      <span className="text-2xs font-bold lg:text-[10px] lg:leading-3 xl:text-sm">{label}</span>
    </button>
  );
}

interface NavCentreProps {
  /** The disc art (an <img> or similar) — sized to fill its wrapper. */
  children: ReactNode;
  label: string;
  ariaLabel: string;
  onSelect: () => void;
  /** Hide from `lg` up — e.g. a camera-only action with no desktop
   *  equivalent (parent's Scan disc: no camera on desktop). */
  hideFromLg?: boolean;
  /** From `lg` the raised disc art is replaced by a glyph so the row reads
   *  like every other sidebar item (outline at rest, filled + `primary`
   *  while `active`) instead of a permanently lit brand disc. */
  desktopIcon?: ReactNode;
  desktopActiveIcon?: ReactNode;
  /** Whether the action's surface is open (the Ask sheet) — drives the
   *  filled glyph and tint on desktop only. */
  active?: boolean;
}

export function NavCentre({
  children,
  label,
  ariaLabel,
  onSelect,
  hideFromLg,
  desktopIcon,
  desktopActiveIcon,
  active = false,
}: NavCentreProps) {
  // The whole cell is the button (like NavItem) so the tap target is the
  // full row/column, not just the raised disc — ≥56px on phone, 56×56 (or a
  // ≥44px-tall row from xl) on desktop.
  const desktopGlyph = active && desktopActiveIcon ? desktopActiveIcon : desktopIcon;
  return (
    <button
      onClick={onSelect}
      aria-label={ariaLabel}
      aria-expanded={desktopIcon ? active : undefined}
      className={`flex-1 flex flex-col items-center justify-end pb-3 rounded-md lg:flex-none lg:size-14 lg:justify-center lg:pb-0 xl:size-auto xl:w-full xl:flex-row xl:justify-start xl:min-h-11 xl:px-3 xl:gap-3 hover:bg-bg ${
        desktopGlyph ? (active ? 'lg:text-primary lg:bg-bg' : 'lg:text-muted') : ''
      } ${hideFromLg ? 'lg:hidden' : ''}`}
    >
      <span
        // Overlaps the bar's top edge on phone/tablet rather than floating
        // clear of it, so it reads as part of the nav and not a detached
        // FAB. From `lg` the nav is a sidebar, so the disc drops the raised
        // treatment and sits inline like every other row — or gives way to
        // the desktop glyph entirely when one is provided.
        className={`block -mt-4 size-14 rounded-pill border-4 border-surface shadow-lg transition-transform active:scale-95 lg:mt-0 lg:size-6 lg:border-0 lg:shadow-none ${
          desktopGlyph ? 'lg:hidden' : ''
        }`}
      >
        {children}
      </span>
      {desktopGlyph && <span className="hidden lg:block">{desktopGlyph}</span>}
      <span
        className={`mt-0.5 text-2xs font-bold text-muted lg:mt-0 lg:text-[10px] xl:text-sm ${
          desktopGlyph ? (active ? 'lg:text-primary' : 'lg:text-muted') : ''
        }`}
      >
        {label}
      </span>
    </button>
  );
}

interface NavLogoProps {
  mark: string;
  wordmark: string;
  alt: string;
}

export function NavLogo({ mark, wordmark, alt }: NavLogoProps) {
  // Sized to the nav's own type: the mark sits at 32px over the 24px glyphs;
  // the wordmark at 24px reads as a peer of the 14px labels beside it rather
  // than a masthead over them.
  return (
    <div className="hidden lg:flex items-center justify-center h-8 mb-4 xl:h-6 xl:justify-start xl:px-3">
      <img src={mark} alt={alt} className="h-8 xl:hidden" />
      <img src={wordmark} alt={alt} className="hidden h-6 xl:block" />
    </div>
  );
}
